import React from 'react'
import { Button, Table, Tooltip, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import styled from 'styled-components'
import { t } from '@/i18n'
import { UseCaseItem, RISK_LEVELS, RESOURCE_LEVELS } from '../../types/usecase'
import { createFilterColumn } from '@/components/filter-dropdown-select'
import Scrollbars from 'react-custom-scrollbars'
import { ResourceLevel, RiskLevel } from '@/pages/program/temporary/widgets'
import { formatDateTime } from '@/utils/date'

const { Text } = Typography

interface UseCaseTableProps {
  data: UseCaseItem[]
  loading: boolean
  pagination: {
    current: number
    pageSize: number
    total: number
  }
  selectedKeys: string[]
  attackTypes: any[]
  onSelectionChange: (useCaseId: string, selected: boolean) => void
  onTableChange: (pagination: any, filters: any, sorter: any) => void
  onTableSelectChange: (selectedRowKeys: React.Key[]) => void
  onDetailClick: (resourceId: string) => void
}

const UseCaseTable: React.FC<UseCaseTableProps> = ({
  data,
  loading,
  pagination,
  selectedKeys,
  attackTypes,
  onSelectionChange,
  onTableChange,
  onTableSelectChange,
  onDetailClick,
}) => {
  const columns: ColumnsType<UseCaseItem> = [
    {
      title: t('用例名称'),
      dataIndex: 'name',
      key: 'name',
      width: 180,
      fixed: 'left',
      render: (text: string) => (
        <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 180 }}>
          {text}
        </Text>
      ),
    },
    createFilterColumn({
      title: t('风险等级'),
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 100,
      options: [...RISK_LEVELS],
      placeholder: t('请选择风险等级'),
      render: (riskLevel: string) => {
        const level = RISK_LEVELS.find(l => l.value === riskLevel)
        return <RiskLevel level={level?.value || ''} />
      },
    }),
    createFilterColumn({
      title: t('用例分级'),
      dataIndex: 'resourceLevel',
      key: 'resourceLevel',
      width: 100,
      options: [...RESOURCE_LEVELS],
      placeholder: t('请选择用例分级'),
      render: (resourceLevel: string) => {
        const cls = RESOURCE_LEVELS.find(c => c.value === resourceLevel)
        return <ResourceLevel level={cls?.value || ''} />
      },
    }),
    createFilterColumn({
      title: t('攻击类型'),
      dataIndex: 'attackTypeId',
      key: 'attackTypeId',
      width: 120,
      options: attackTypes.map(type => ({
        label: type.name,
        value: type.id,
      })),
      placeholder: t('请选择攻击类型'),
      render: (attackTypeId: string) => {
        const attackType = attackTypes.find(type => type.id === attackTypeId)
        return (
          <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 120 }}>
            {attackType?.name || '-'}
          </Text>
        )
      },
    }),

    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 120,
      sorter: true,
      sortDirections: ['descend' as const, 'ascend' as const],
      render: (text: string) => (
        <Tooltip title={formatDateTime(text, 'standard')}>{formatDateTime(text, 'mdhm')}</Tooltip>
      ),
    },
    {
      title: t('操作'),
      key: 'action',
      width: 60,
      fixed: 'right',
      render: (_text, record) => (
        <Button type="link" onClick={() => onDetailClick(record.resourceId)}>
          {t('详情')}
        </Button>
      ),
    },
  ]

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: onTableSelectChange,
    onSelect: (record: UseCaseItem, selected: boolean) => {
      onSelectionChange(record.resourceId, selected)
    },
    onSelectAll: (selected: boolean, _selectedRows: UseCaseItem[], changeRows: UseCaseItem[]) => {
      changeRows.forEach(row => {
        onSelectionChange(row.resourceId, selected)
      })
    },
  }

  return (
    <div style={{ height: '550px' }}>
      <Scrollbars>
        <StyledTable
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="resourceId"
          rowSelection={rowSelection}
          onChange={onTableChange}
          pagination={{
            total: pagination.total,
            showTotal: total => (
              <span>
                {t('共')}
                <span style={{ color: '#4238c9' }}>{total}</span>
                {t('条')}
              </span>
            ),
            pageSize: pagination.pageSize,
            current: pagination.current,
            showQuickJumper: true,
            showSizeChanger: true,
          }}
          scroll={{ x: 300 }}
        />
      </Scrollbars>
    </div>
  )
}

export default UseCaseTable

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
    font-weight: 500;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f5f5f5;
  }

  .ant-table-measure-row {
    display: none;
  }

  .ant-table-tbody .ant-btn-link {
    padding: 6.6px 0;
  }
`
